<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-bell-outline me-2"></i>{{ $t('page.notifications_in', 'Notifications') }}</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="hms-btn hms-btn-blue" @click="generateAlerts" :disabled="processing">
            <i class="mdi mdi-radar me-1"></i>{{ processing ? 'Scanning...' : 'Scan System' }}
          </button>
          <button class="hms-btn hms-btn-amber" @click="markAllRead" :disabled="stats.unread === 0">
            <i class="mdi mdi-email-open-multiple me-1"></i>Mark All Read
          </button>
          <button class="hms-btn hms-btn-primary" @click="openCompose" v-if="canBroadcast">
            <i class="mdi mdi-bullhorn me-1"></i>Broadcast
          </button>
        </div>
      </div>

      <!-- Stats row -->
      <div class="hms-stat-row">
        <div class="hms-stat" @click="filterUnread = false; filterType = ''" :class="{ selected: !filterUnread && !filterType }">
          <div class="hms-stat-num">{{ stats.total }}</div>
          <div class="hms-stat-lbl">All</div>
        </div>
        <div class="hms-stat" @click="filterUnread = true; filterType = ''" :class="{ selected: filterUnread && !filterType }">
          <div class="hms-stat-num hms-stat-info">{{ stats.unread }}</div>
          <div class="hms-stat-lbl">Unread</div>
        </div>
        <div class="hms-stat" v-for="t in (stats.by_type || [])" :key="t.type"
             @click="filterType = (filterType === t.type ? '' : t.type)"
             :class="{ selected: filterType === t.type }">
          <div class="hms-stat-num" :class="typeColor(t.type)"><i class="mdi" :class="typeIcon(t.type)"></i></div>
          <div class="hms-stat-lbl">{{ formatType(t.type) }} <span class="hms-muted-sm">({{ t.unread }}/{{ t.c }})</span></div>
        </div>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else>
        <div class="hms-card mb-3">
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <div class="hms-search-wrap">
              <i class="mdi mdi-magnify"></i>
              <input class="hms-search" v-model="search" placeholder="Search title or message..." @input="debouncedLoad" />
            </div>
            <label class="hms-toggle">
              <input type="checkbox" :checked="filterUnread" @change="filterUnread = $event.target.checked; load()" />
              <span>Unread only</span>
            </label>
            <span class="hms-muted ms-auto">{{ notifications.length }} of {{ stats.total }}</span>
          </div>
        </div>

        <div v-if="notifications.length" class="hms-notif-list">
          <div v-for="n in notifications" :key="n.notification_id"
               class="hms-notif" :class="{ unread: n.is_read != 1 }">
            <div class="hms-notif-icon" :class="typeColor(n.type)">
              <i class="mdi" :class="typeIcon(n.type)"></i>
            </div>
            <div class="hms-notif-body" @click="openDetail(n)">
              <div class="d-flex align-items-center gap-2">
                <span class="hms-notif-title">{{ n.title || '(no title)' }}</span>
                <span class="hms-badge" :class="typeBadge(n.type)">{{ formatType(n.type) }}</span>
                <span v-if="n.is_read != 1" class="hms-badge hms-badge-info">new</span>
              </div>
              <div class="hms-notif-msg" v-if="n.message">{{ truncate(n.message, 220) }}</div>
              <div class="hms-muted-sm mt-1">
                <i class="mdi mdi-clock-outline"></i> {{ relativeTime(n.created_at) }}
                <span v-if="n.reference_type" class="ms-2">
                  <i class="mdi mdi-link-variant"></i> {{ n.reference_type }}{{ n.reference_id ? ' #' + n.reference_id : '' }}
                </span>
              </div>
            </div>
            <div class="hms-notif-actions">
              <button v-if="n.is_read != 1" class="hms-btn hms-btn-sm hms-btn-blue" @click.stop="markRead(n)" title="Mark read">
                <i class="mdi mdi-email-open"></i>
              </button>
              <button class="hms-btn hms-btn-sm hms-btn-danger" @click.stop="confirmDelete(n)" title="Delete">
                <i class="mdi mdi-trash-can"></i>
              </button>
            </div>
          </div>
        </div>
        <div v-else class="hms-card hms-empty">
          <i class="mdi mdi-bell-off-outline"></i>{{ filterUnread || filterType || search ? 'No notifications match.' : 'No notifications yet.' }}
        </div>
      </div>

      <!-- DETAIL MODAL -->
      <div class="hms-modal-overlay" v-if="showDetail && detail" @click.self="showDetail=false">
        <div class="hms-modal hms-modal-sm">
          <div class="hms-modal-title">
            <span class="d-flex align-items-center gap-2">
              <span class="hms-notif-icon hms-notif-icon-lg" :class="typeColor(detail.type)">
                <i class="mdi" :class="typeIcon(detail.type)"></i>
              </span>
              <span>{{ detail.title }}</span>
            </span>
            <button class="hms-modal-close" @click="showDetail=false">&times;</button>
          </div>

          <div class="hms-modal-meta">
            <span class="hms-badge" :class="typeBadge(detail.type)">{{ formatType(detail.type) }}</span>
            <span class="hms-muted-sm ms-2"><i class="mdi mdi-clock-outline"></i> {{ formatDateTime(detail.created_at) }}</span>
          </div>

          <div class="hms-detail-msg" v-if="detail.message">{{ detail.message }}</div>

          <div v-if="detail.reference_type" class="hms-info-banner mt-3">
            <i class="mdi mdi-link-variant me-1"></i>Reference: <strong>{{ detail.reference_type }}</strong>{{ detail.reference_id ? ' #' + detail.reference_id : '' }}
            <button v-if="referenceRoute(detail.reference_type)" class="hms-btn hms-btn-sm hms-btn-blue ms-2" @click="goToReference(detail)">
              <i class="mdi mdi-arrow-right"></i> Open
            </button>
          </div>

          <div class="d-flex gap-2 mt-3">
            <button v-if="detail.is_read != 1" class="hms-btn hms-btn-blue flex-fill" @click="markRead(detail); showDetail = false">
              <i class="mdi mdi-email-open me-1"></i>Mark Read
            </button>
            <button class="hms-btn hms-btn-danger flex-fill" @click="confirmDelete(detail); showDetail = false">
              <i class="mdi mdi-trash-can me-1"></i>Delete
            </button>
          </div>
        </div>
      </div>

      <!-- COMPOSE / BROADCAST MODAL -->
      <div class="hms-modal-overlay" v-if="showCompose" @click.self="showCompose=false">
        <div class="hms-modal">
          <div class="hms-modal-title">
            <span><i class="mdi mdi-bullhorn me-1"></i>Broadcast Notification</span>
            <button class="hms-modal-close" @click="showCompose=false">&times;</button>
          </div>
          <div v-if="composeError" class="hms-alert hms-alert-danger"><i class="mdi mdi-alert-circle me-1"></i>{{ composeError }}</div>

          <div class="row g-3 mb-3">
            <div class="col-md-6"><label class="hms-label">Audience</label>
              <select class="hms-input" v-model="compose.scope">
                <option value="user">Specific user</option>
                <option value="branch">All users at branch</option>
                <option value="hospital">Everyone in the hospital</option>
              </select>
            </div>
            <div class="col-md-6" v-if="compose.scope === 'user'">
              <label class="hms-label">User</label>
              <select class="hms-input" v-model="compose.target_id">
                <option value="">— Select —</option>
                <option v-for="u in users" :key="u.user_id" :value="u.user_id">
                  {{ u.first_name }} {{ u.last_name }} (@{{ u.username }})
                </option>
              </select>
            </div>
            <div class="col-md-6" v-if="compose.scope === 'branch'">
              <label class="hms-label">Branch</label>
              <select class="hms-input" v-model="compose.target_id">
                <option value="">— Select —</option>
                <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Type</label>
              <select class="hms-input" v-model="compose.type">
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warning">Warning</option>
                <option value="alert">Alert</option>
                <option value="danger">Danger</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Title *</label>
              <input class="hms-input" v-model="compose.title" placeholder="Short headline" />
            </div>
            <div class="col-12"><label class="hms-label">Message</label>
              <textarea class="hms-input" rows="4" v-model="compose.message" placeholder="Full message body..."></textarea>
            </div>
          </div>

          <button class="hms-btn hms-btn-primary w-100" @click="sendBroadcast" :disabled="processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
            {{ processing ? 'Sending...' : 'Send' }}
          </button>
        </div>
      </div>

      <!-- Toast -->
      <transition name="hms-toast">
        <div v-if="toastMsg" class="hms-toast" :class="{ 'hms-toast-error': toastError }">
          <i class="mdi me-2" :class="toastError ? 'mdi-alert-circle' : 'mdi-check-circle'"></i>{{ toastMsg }}
        </div>
      </transition>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted { color: var(--hms-s); font-size: 13px; }
.hms-muted-sm { color: var(--hms-s); font-size: 12px; }

.hms-stat-row { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 20px; }
.hms-stat { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 18px; flex: 1; min-width: 130px; cursor: pointer; transition: all .15s; }
.hms-stat:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.hms-stat.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt); }
.hms-stat-num { font-size: 22px; font-weight: 800; color: var(--hms-primary); line-height: 1.1; }
.hms-stat-lbl { font-size: 12px; color: var(--hms-s); margin-top: 4px; }
.hms-stat-info { color: var(--hms-info); }
.hms-stat-warning { color: var(--hms-warning); }
.hms-stat-danger { color: var(--hms-danger); }
.hms-stat-success { color: var(--hms-success); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 18px; margin-bottom: 16px; }
.hms-spinner { display: flex; justify-content: center; padding: 40px; }
.hms-spinner .spinner-border { color: var(--hms-primary); }
.hms-empty { text-align: center; padding: 40px 20px; color: var(--hms-s); }
.hms-empty i { font-size: 48px; color: var(--hms-br); display: block; margin-bottom: 12px; }

.hms-search-wrap { position: relative; flex: 1; min-width: 220px; max-width: 340px; }
.hms-search-wrap i { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--hms-s); }
.hms-search { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px 8px 36px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-search:focus { border-color: var(--hms-primary); }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; color: var(--hms-h); font-size: 13px; }
.hms-toggle input { width: 18px; height: 18px; accent-color: var(--hms-primary); }

.hms-notif-list { display: flex; flex-direction: column; gap: 8px; }
.hms-notif { display: flex; gap: 12px; background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 14px 16px; transition: all .15s; }
.hms-notif:hover { border-color: var(--hms-primary); }
.hms-notif.unread { border-left: 4px solid var(--hms-primary); background: var(--hms-bg1); }

.hms-notif-icon { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 18px; }
.hms-notif-icon-lg { width: 44px; height: 44px; font-size: 22px; }
.hms-notif-icon.hms-stat-info    { background: rgba(23,162,184,.15); color: var(--hms-info); }
.hms-notif-icon.hms-stat-warning { background: rgba(255,193,7,.15);  color: var(--hms-warning); }
.hms-notif-icon.hms-stat-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-notif-icon.hms-stat-success { background: rgba(40,167,69,.15);  color: var(--hms-success); }

.hms-notif-body { flex: 1; min-width: 0; cursor: pointer; }
.hms-notif-title { color: var(--hms-h); font-weight: 700; font-size: 14px; }
.hms-notif-msg { color: var(--hms-s); font-size: 13px; margin-top: 4px; line-height: 1.4; }
.hms-notif-actions { display: flex; align-items: flex-start; gap: 4px; flex-shrink: 0; }

.hms-badge { padding: 2px 8px; border-radius: 50px; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; display: inline-block; }
.hms-badge-info    { background: rgba(23,162,184,.15); color: var(--hms-info); }
.hms-badge-warning { background: rgba(255,193,7,.15);  color: var(--hms-warning); }
.hms-badge-danger  { background: rgba(220,53,69,.15);  color: var(--hms-danger); }
.hms-badge-success { background: rgba(40,167,69,.15);  color: var(--hms-success); }
.hms-badge-neutral { background: var(--hms-bg3); color: var(--hms-m); }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.hms-btn-sm { padding: 5px 10px; font-size: 12px; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }
.hms-btn-amber { background: rgba(245,158,11,.15); color: #f59e0b; border: 1px solid rgba(245,158,11,.3); }
.hms-btn-amber:hover:not(:disabled) { background: rgba(245,158,11,.25); }
.hms-btn-danger { background: rgba(220,53,69,.15); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }
.hms-btn-danger:hover:not(:disabled) { background: rgba(220,53,69,.25); }

.hms-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 200; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.hms-modal { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 16px; padding: 24px; width: 100%; max-width: 720px; max-height: calc(100vh - 80px); overflow-y: auto; box-shadow: 0 24px 60px rgba(0,0,0,.3); }
.hms-modal-sm { max-width: 520px; }
.hms-modal-title { color: var(--hms-h); font-size: 18px; font-weight: 700; margin-bottom: 18px; display: flex; align-items: center; justify-content: space-between; }
.hms-modal-close { color: var(--hms-s); cursor: pointer; font-size: 24px; line-height: 1; background: none; border: none; padding: 0 4px; }
.hms-modal-meta { margin-bottom: 14px; }
.hms-detail-msg { color: var(--hms-h); font-size: 14px; line-height: 1.6; white-space: pre-wrap; padding: 14px 16px; background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; }

.hms-info-banner { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); border-radius: 8px; padding: 10px 14px; font-size: 13px; }
.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-toast { position: fixed; bottom: 24px; right: 24px; background: var(--hms-bg1); border: 1px solid var(--hms-success); color: var(--hms-h); padding: 12px 18px; border-radius: 10px; box-shadow: 0 12px 32px rgba(40,167,69,.2); z-index: 1000; font-size: 13px; display: flex; align-items: center; }
.hms-toast i { color: var(--hms-success); }
.hms-toast.hms-toast-error { border-color: var(--hms-danger); box-shadow: 0 12px 32px rgba(220,53,69,.2); }
.hms-toast.hms-toast-error i { color: var(--hms-danger); }
.hms-toast-enter-active, .hms-toast-leave-active { transition: all .25s ease; }
.hms-toast-enter-from, .hms-toast-leave-to { opacity: 0; transform: translateY(20px); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

const REF_ROUTES = {
  pharmacy: "/pharmacy", lab: "/lab", radiology: "/radiology",
  wards: "/wards", appointments: "/appointments", queue: "/queue",
  patient: "/patients", admission: "/wards", invoice: "/billing"
};

export default {
  name: "NotificationsInbox",
  components: { Navigation },
  data() {
    return {
      loading: true, processing: false,
      notifications: [],
      stats: { total: 0, unread: 0, by_type: [] },

      search: "", filterUnread: false, filterType: "",
      _searchTimer: null,

      detail: null, showDetail: false,

      showCompose: false,
      compose: { scope: "user", target_id: "", title: "", message: "", type: "info" },
      composeError: "",
      users: [], branches: [],
      roleCode: "",

      toastMsg: "", toastError: false, toastTimer: null
    };
  },
  computed: {
    canBroadcast() {
      var c = (this.roleCode || "").toLowerCase();
      return c === "super_admin" || c === "hospital_admin" || c === "branch_manager";
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
    this.roleCode = sessionStorage.getItem("roleCode") || "";
    this.load();
  },
  beforeUnmount() {
    if (this.toastTimer)   clearTimeout(this.toastTimer);
    if (this._searchTimer) clearTimeout(this._searchTimer);
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    showToast(msg, isError) {
      this.toastMsg = msg; this.toastError = !!isError;
      if (this.toastTimer) clearTimeout(this.toastTimer);
      var self = this;
      this.toastTimer = setTimeout(() => { self.toastMsg = ""; }, 2400);
    },

    debouncedLoad() {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      var self = this;
      this._searchTimer = setTimeout(() => { self.load(); }, 350);
    },

    async load() {
      this.loading = true;
      try {
        var payload = { function: "getNotifications", limit: 200 };
        if (this.filterUnread) payload.unread_only = 1;
        if (this.filterType)   payload.type = this.filterType;
        if (this.search)       payload.search = this.search;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) {
          this.notifications = (r.data.data && r.data.data.Notifications) || [];
          this.stats         = (r.data.data && r.data.data.Stats) || { total: 0, unread: 0, by_type: [] };
        }
      } catch(e) { console.error(e); }
      this.loading = false;
    },

    formatType(t) {
      if (!t) return "Other";
      return t.charAt(0).toUpperCase() + t.slice(1).replace(/_/g, ' ');
    },
    typeIcon(t) {
      var m = { info: "mdi-information-outline", warning: "mdi-alert-outline", danger: "mdi-alert-octagon-outline",
                alert: "mdi-bell-alert-outline", success: "mdi-check-circle-outline" };
      return m[t] || "mdi-bell-outline";
    },
    typeColor(t) {
      var m = { info: "hms-stat-info", warning: "hms-stat-warning", danger: "hms-stat-danger",
                alert: "hms-stat-warning", success: "hms-stat-success" };
      return m[t] || "hms-stat-info";
    },
    typeBadge(t) {
      var m = { info: "hms-badge-info", warning: "hms-badge-warning", danger: "hms-badge-danger",
                alert: "hms-badge-warning", success: "hms-badge-success" };
      return m[t] || "hms-badge-neutral";
    },
    truncate(s, n) { if (!s) return ""; return s.length > n ? s.substring(0, n) + "..." : s; },
    formatDateTime(s) { if (!s) return "—"; var d = new Date(s.replace ? s.replace(" ","T") : s); return isNaN(d) ? s : d.toLocaleString(); },
    relativeTime(s) {
      if (!s) return "—";
      var d = new Date(s.replace ? s.replace(" ","T") : s); if (isNaN(d)) return s;
      var diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60)    return Math.floor(diff) + "s ago";
      if (diff < 3600)  return Math.floor(diff/60) + "m ago";
      if (diff < 86400) return Math.floor(diff/3600) + "h ago";
      if (diff < 604800)return Math.floor(diff/86400) + "d ago";
      return d.toLocaleDateString();
    },

    referenceRoute(refType) { return REF_ROUTES[(refType || '').toLowerCase()]; },
    goToReference(n) {
      var route = this.referenceRoute(n.reference_type);
      if (route) this.$router.push(route);
    },

    openDetail(n) {
      this.detail = n;
      this.showDetail = true;
      if (n.is_read != 1) this.markRead(n);
    },

    async markRead(n) {
      try {
        var r = await executeJson({ function: "markNotificationRead", notification_id: n.notification_id }, hms);
        if (r.data && r.data.success) {
          n.is_read = 1; n.read_at = new Date().toISOString();
          this.stats.unread = Math.max(0, this.stats.unread - 1);
        }
      } catch(e) {}
    },
    async markAllRead() {
      try {
        var r = await executeJson({ function: "markAllRead" }, hms);
        if (r.data && r.data.success) { this.showToast("All marked as read"); await this.load(); }
      } catch(e) { this.showToast("Connection error", true); }
    },
    async confirmDelete(n) {
      if (!confirm("Delete this notification?")) return;
      try {
        var r = await executeJson({ function: "deleteNotification", notification_id: n.notification_id }, hms);
        if (r.data && r.data.success) { this.showToast("Notification deleted"); await this.load(); }
        else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
    },
    async generateAlerts() {
      this.processing = true;
      try {
        var r = await executeJson({ function: "generateInboxAlerts" }, hms);
        if (r.data && r.data.success) {
          var n = (r.data.data && r.data.data.created) || 0;
          this.showToast(n + " new alert(s) generated");
          await this.load();
        } else { this.showToast(r.data.message || "Failed", true); }
      } catch(e) { this.showToast("Connection error", true); }
      this.processing = false;
    },

    async openCompose() {
      this.compose = { scope: "user", target_id: "", title: "", message: "", type: "info" };
      this.composeError = "";
      this.showCompose = true;
      // Lazy load lists
      try {
        var res = await Promise.all([
          executeJson({ function: "getUsers" }, hms),
          executeJson({ function: "getBranches" }, hms)
        ]);
        if (res[0].data && res[0].data.success) this.users    = (res[0].data.data && res[0].data.data.Users)    || [];
        if (res[1].data && res[1].data.success) this.branches = (res[1].data.data && res[1].data.data.Branches) || [];
      } catch(e) {}
    },
    async sendBroadcast() {
      this.composeError = "";
      var c = this.compose;
      if (!c.title) { this.composeError = "Title is required."; return; }
      if (c.scope !== 'hospital' && !c.target_id) { this.composeError = "Select a target."; return; }
      this.processing = true;
      try {
        var r = await executeJson(Object.assign({ function: "createNotification" }, c), hms);
        if (r.data && r.data.success) {
          var n = (r.data.data && r.data.data.delivered) || 0;
          this.showToast("Sent to " + n + " recipient(s)");
          this.showCompose = false;
          if (c.scope === 'user' && String(c.target_id) === String(sessionStorage.getItem("userId"))) await this.load();
          else if (c.scope === 'hospital' || c.scope === 'branch') await this.load();
        } else { this.composeError = r.data.message || "Failed."; }
      } catch(e) { this.composeError = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
